import { useAsync } from '@nxsys/hooks/useAsync';
import {
  exportXeroJournalApi,
  getXeroSettingsApi,
  previewXeroJournalApi,
  updateXeroSettingsApi,
} from '@nxsys/services/api/accounting';
import {
  UpdateXeroSettingsParams,
  XeroJournalExportResponse,
  XeroJournalPreview,
  XeroJournalRequest,
  XeroSettingsResponse,
} from '@nxsys/types/api/accounting';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';

const useErrorToast = (error: Error | null) => {
  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);
};

export const useGetXeroSettings = () => {
  const { value, loading, error, run } = useAsync<XeroSettingsResponse, string>(
    getXeroSettingsApi,
  );
  useErrorToast(error);
  return { value, loading, error, run };
};

export const useUpdateXeroSettings = () => {
  const { value, loading, error, run } = useAsync<
    XeroSettingsResponse,
    UpdateXeroSettingsParams & { companyId: string }
  >(({ companyId, ...params }) => updateXeroSettingsApi(companyId, params));
  useErrorToast(error);
  const _run = (companyId: string, params: UpdateXeroSettingsParams) =>
    run({ companyId, ...params });
  return { value, loading, error, run: _run };
};

export const usePreviewXeroJournal = () => {
  const { value, loading, error, run } = useAsync<
    XeroJournalPreview,
    XeroJournalRequest
  >(previewXeroJournalApi);
  useErrorToast(error);
  return { value, loading, error, run };
};

export const useExportXeroJournal = () => {
  const { value, loading, error, run } = useAsync<
    XeroJournalExportResponse,
    XeroJournalRequest
  >(exportXeroJournalApi);
  useErrorToast(error);
  return { value, loading, error, run };
};
